import React, { useState, useEffect } from 'react';
import './PricingPlans.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faTimesCircle, 
  faHeadset, 
  faUsers, 
  faBolt, 
  faPaintBrush, 
  faGem, 
  faRocket,
  faCrown,
  faShieldAlt,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { api, ENDPOINTS } from '../../services/apiConfig';



const PricingPlans = () => {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Add state for confirmation popup
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Fetch subscriptions from the API
  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        // Fetch subscription data from the endpoint
        const data = await api.business.get(ENDPOINTS.business.showallsubscription);
        
        // The data is already parsed, so we can use it directly
        console.log('Subscription data:', data);
        
        setSubscriptions(data);
        toast.success('Subscription plans loaded successfully!');
      } catch (err) {
        console.error('Full error object:', err);
        const errorMessage = err?.data?.message || err.message;
        setError(errorMessage);
        toast.error(`Error loading plans: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    };
  
    fetchSubscriptions();
  }, []);

  // Handle showing confirmation popup
  const handleShowConfirmation = (plan) => {
    setSelectedPlan(plan);
    setShowConfirmation(true);
  };

  // Handle proceeding with subscription
  const handleProceed = async () => {
    if (!selectedPlan) return;
    
    try {
      toast.info(`Processing ${selectedPlan} subscription selection...`, {
        position: 'top-center',
        autoClose: 2000,
      });
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found. Please log in again.', {
          position: 'top-center',
          autoClose: 5000,
        });
        return;
      }
      
      // Create proper payload with both subscriptionTypeEnum and subscriptionDurationEnum
      const payload = {
        subscriptionTypeEnum: selectedPlan, // e.g., "STANDARD", "EXECUTIVE", "PREMIUM"
        subscriptionDurationEnum: billingPeriod.toUpperCase() // "MONTHLY" or "YEARLY"
      };
      
      // Make API call
      const result = await api.business.post(
        ENDPOINTS.business.vendorselectsubscription,
        payload
      );

      console.log('Subscription selected successfully:', result);
      
      // Show success toast
      toast.success(`${selectedPlan} subscription selected successfully!`, {
        position: 'top-center',
        autoClose: 2000,
      });
      
      // Close popup and navigate to payment page
      setShowConfirmation(false);
      navigate("/d/proceed-to-make-payment");
      
    } catch (err) {
      console.error('Error selecting subscription:', err);
      
      // Handle specific errors
      if (err.response && err.response.data) {
        const errorMessage = err.response.data.detail || err.response.data.message || 'Unknown error';
        
        // Check for business not found error
        if (errorMessage === "Business not found for the provided vendor") {
          toast.error(errorMessage, {
            position: 'top-center',
            autoClose: 5000,
          });
        } else {
          toast.error(`Failed to select subscription: ${errorMessage}`, {
            position: 'top-center',
            autoClose: 5000,
          });
        }
      } else {
        // Handle unexpected errors
        toast.error(`Failed to select subscription: ${err.message}`, {
          position: 'top-center',
          autoClose: 5000,
        });
      }
      
      // Close popup even on error
      setShowConfirmation(false);
    }
  };

  // Handle canceling subscription
  const handleCancel = () => {
    setShowConfirmation(false);
    setSelectedPlan(null);
  };

  // Handle billing period change
  const handleBillingPeriodChange = (newPeriod) => {
    setBillingPeriod(newPeriod);
    toast.info(`Billing period changed to ${newPeriod}`, {
      position: 'top-center',
      autoClose: 2000,
    });
  };

  if (loading) {
    return (
      <div className="pricing-module-container">
        <div className="pricing-loading">
          <FontAwesomeIcon icon={faRocket} spin className="pricing-loading-icon" />
          <span className="pricing-loading-text">Loading plans...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pricing-module-container">
        <div className="pricing-error">
          <FontAwesomeIcon icon={faTimesCircle} className="pricing-error-icon" />
          <span>Error: {error}</span>
        </div>
      </div>
    );
  }

  // Map feature icons
  const featureIcons = {
    support: faHeadset,
    features: faGem,
    users: faUsers,
    processing: faBolt,
    customization: faPaintBrush,
    perks: faCrown
  };

  // Define plan features and details - these won't change based on API data
  const planFeatures = {
    'STANDARD': {
      id: 'standard',
      name: 'Standard',
      icon: faShieldAlt,
      features: [
        { text: 'Basic support (call and email only)', icon: 'support' },
        { text: 'Access to limited features', icon: 'features' },
        { text: '1 user account', icon: 'users' },
        { text: '1 user ad services', icon: 'users' },
        { text: 'Standard processing time', icon: 'processing' },
      ],
      isRecommended: false,
      featureStatus: [true, true, true, true, true]
    },
    'EXECUTIVE': {
      id: 'executive',
      name: 'Executive',
      icon: faRocket,
      features: [
        { text: 'Priority support (email,chat & phone)', icon: 'support' },
        { text: 'Access to all core features', icon: 'features' },
        { text: '2 user accounts', icon: 'users' },
        { text: '2 ads  service', icon: 'users' },
        { text: 'Faster processing time', icon: 'processing' },
      ],
      isRecommended: true,
      featureStatus: [true, true, true, true, true]
    },
    'PREMIUM': {
      id: 'premium',
      name: 'Premium',
      icon: faCrown,
      features: [
        { text: '24/7 dedicated support (email, chat & phone)', icon: 'support' },
        { text: 'Access to all features + premium add-ons', icon: 'features' },
        { text: 'Unlimited user accounts', icon: 'users' },
        { text: 'Unlimited ads services', icon: 'users' },
        { text: 'Fastest processing time', icon: 'processing' },
        { text: 'Full customization options', icon: 'customization' },
        { text: 'Exclusive perks (early access to new features)', icon: 'perks' },
      ],
      isRecommended: false,
      featureStatus: [true, true, true, true, true, true, true]
    }
  };

  // Calculate yearly savings percentage (actually using the data from API)
  const calculateSavingsPercent = (monthlyPrice, yearlyPrice) => {
    const monthlyCostForYear = monthlyPrice * 12;
    return Math.round((monthlyCostForYear - yearlyPrice) / monthlyCostForYear * 100);
  };

  return (
    <div className="pricing-module-container">
      <ToastContainer />
      
      {/* Confirmation Popup */}
      {showConfirmation && (
        <div className="popup-overlay">
          <div className="popup-content">
            <div className="popup-header">
              <FontAwesomeIcon icon={faRocket} className="popup-icon" />
              <h2>Subscription Confirmation</h2>
            </div>
            <p>You just selected  {selectedPlan} plan. Proceed to make payment?</p>
            <div className="popup-buttons">
              <button className="popup-btn cancel" onClick={handleCancel}>
                Cancel
              </button>
              <button className="popup-btn proceed" onClick={handleProceed}>
                Proceed to Payment <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="pricing-content-wrapper">
        <h1 className="pricing-main-heading">Select The Best Plan For Your Needs</h1>
        
        <div className="pricing-toggle-container">
          <span className={`pricing-toggle-option ${billingPeriod === 'monthly' ? 'pricing-active' : ''}`}>
            Monthly Billing
          </span>
          <label className="pricing-switch">
            <input 
              type="checkbox" 
              checked={billingPeriod === 'yearly'} 
              onChange={() => handleBillingPeriodChange(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
            />
            <span className="pricing-slider pricing-round"></span>
          </label>
          <span className={`pricing-toggle-option ${billingPeriod === 'yearly' ? 'pricing-active' : ''}`}>
            Yearly Billing {billingPeriod === 'yearly' && subscriptions.length > 0 &&
              <span className="pricing-save-badge">
                <FontAwesomeIcon icon={faGem} className="pricing-save-icon" /> 
                Save up to {Math.max(...subscriptions.map(sub => 
                  calculateSavingsPercent(sub.monthly_price, sub.yearly_price)
                ))}%
              </span>
            }
          </span>
        </div>

        <div className="pricing-cards-container">
          {subscriptions.map((subscription) => {
            // Get the plan features based on the plan type
            const plan = planFeatures[subscription.plan];
            if (!plan) return null;
            
            // Calculate prices based on billing period
            const monthlyPrice = subscription.monthly_price;
            const yearlyPrice = subscription.yearly_price;
            
            // Calculate the display price based on billing period
            // For monthly billing, show monthly price; for yearly billing, show yearly price
            const displayPrice = billingPeriod === 'monthly' ? 
              monthlyPrice : 
              yearlyPrice;
              
            // Calculate savings
            const yearlySavingsAmount = (monthlyPrice * 12) - yearlyPrice;
            const savingsPercent = calculateSavingsPercent(monthlyPrice, yearlyPrice);

            return (
              <div key={plan.id} className={`pricing-card ${plan.isRecommended ? 'pricing-recommended' : ''}`}>
                <div className="pricing-card-header">
                  <FontAwesomeIcon icon={plan.icon} className="pricing-plan-icon" />
                  <h2 className="pricing-plan-name">{plan.name}</h2>
                </div>
                
                <div className="pricing-card-content">
                  <div className="pricing-price">
                    GHS {displayPrice.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    <span className="pricing-period">/{billingPeriod === 'monthly' ? 'month' : 'year'}</span>
                  </div>
                  
                  {billingPeriod === 'yearly' && (
                    <div className="pricing-savings">
                      <FontAwesomeIcon icon={faGem} className="pricing-savings-icon" />
                      You save GHS {yearlySavingsAmount.toLocaleString('en-US', {maximumFractionDigits: 0})} ({savingsPercent}%)
                    </div>
                  )}
                  
                  <ul className="pricing-feature-list">
                    {plan.features.map((feature, index) => (
                      <li 
                        key={index} 
                        className={plan.featureStatus[index] ? 'pricing-included' : 'pricing-excluded'}
                      >
                        <span className="pricing-feature-icon">
                          <FontAwesomeIcon 
                            icon={plan.featureStatus[index] ? faCheckCircle : faTimesCircle} 
                            className={`pricing-icon ${plan.featureStatus[index] ? 'pricing-included-icon' : 'pricing-excluded-icon'}`}
                          />
                        </span>
                        <span className="pricing-feature-text">
                          <FontAwesomeIcon 
                            icon={featureIcons[feature.icon]} 
                            className="pricing-feature-type-icon" 
                          /> {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <button 
                  className="pricing-select-button"
                  onClick={() => handleShowConfirmation(plan.id.toUpperCase())}
                >
                  <FontAwesomeIcon icon={faRocket} className="pricing-button-icon" />
                  Select {plan.name}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PricingPlans;